import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { forgetPassword, forgetPasswordCode } from '../../services/auth';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCodeForm, setShowCodeForm] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await forgetPassword(email);
      if (response.statusCode === 200) {
        toast.success(t('forgotPassword.emailSent'));
        setShowCodeForm(true);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('forgotPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    try {
      if (password !== confirmPassword) {
        toast.error(t('forgotPassword.confirmNotMatch'));
        return;
      }

      setIsLoading(true);
      const response = await forgetPasswordCode({
        email,
        code: activationCode,
        newPassword: password
      });

      if (response.statusCode === 200) {
        toast.success(response.message);
        navigate('/login');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('forgotPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        px: 2,
        background: theme.palette.background.default
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={theme.palette.mode === 'dark' ? 3 : 1}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            background: theme.palette.background.paper,
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            gutterBottom
            align="center"
            color="primary"
            sx={{ mb: 3 }}
          >
            {t('forgotPassword.title')}
          </Typography>

          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            {showCodeForm 
              ? t('forgotPassword.codeInstruction')
              : t('forgotPassword.emailTitle')}
          </Typography>

          {!showCodeForm ? (
            <Box component="form" onSubmit={handleEmailSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t('forgotPassword.EmailText')}
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('forgotPassword.buttonText')
                )}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handlePasswordReset} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                name="code"
                label={t('forgotPassword.code')}
                type="text"
                id="code"
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value)}
                inputProps={{ maxLength: 6 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  },
                  '& input': {
                    textAlign: 'center',
                    letterSpacing: '0.5em',
                    fontSize: '1.2em'
                  }
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('forgotPassword.password')}
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={t('forgotPassword.confirmPassword')}
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={password !== confirmPassword && confirmPassword !== ''}
                helperText={
                  password !== confirmPassword && confirmPassword !== ''
                    ? t('forgotPassword.confirmNotMatch')
                    : ''
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('forgotPassword.buttonText')
                )}
              </Button>
            </Box>
          )}

          <Button
            fullWidth
            variant="text"
            onClick={() => navigate('/login')}
            sx={{ mt: 1 }}
          >
            {t('forgotPassword.backToLogin')}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword; 